import { useCallback, useEffect, useMemo, useState } from 'react';

import { changelogs, getLatestChangelog } from '@/assets/changelogs';

import { SET_LAST_CHANGELOG_SEEN } from '../api/user';
import {
  LanguageCode,
  SetLastChangelogSeenMutation,
  SetLastChangelogSeenMutationVariables,
} from '../types';
import { useUser } from './context/user';
import useEnhancedMutation from './use-enhanced-mutation';

export const useChangelogStatus = () => {
  const { user } = useUser();
  const [hasUnreadChanges, setHasUnreadChanges] = useState(false);

  const [setLastChangelogSeen, { loading: isUpdating, error }] = useEnhancedMutation<
    SetLastChangelogSeenMutation,
    SetLastChangelogSeenMutationVariables
  >(SET_LAST_CHANGELOG_SEEN, {
    update: (cache, { data }) => {
      if (!data?.setLastChangelogSeen) return;
      cache.modify({
        id: cache.identify({
          __typename: 'User',
          id: data.setLastChangelogSeen.id,
        }),
        fields: {
          lastChangelogSeen: () => data.setLastChangelogSeen.lastChangelogSeen,
        },
      });
    },
    onCompleted: () => {
      setHasUnreadChanges(false);
    },
  });

  const hasNewerChanges = useMemo(() => {
    if (!user?.lastChangelogSeen) {
      return changelogs.length > 0;
    }

    return changelogs.some((changelog) => {
      const changelogDate = new Date(changelog.date);
      const lastViewed = new Date(user.lastChangelogSeen);
      return changelogDate > lastViewed;
    });
  }, [user?.lastChangelogSeen]);

  useEffect(() => {
    setHasUnreadChanges(hasNewerChanges);
  }, [hasNewerChanges]);

  const markChangelogsAsRead = useCallback(async () => {
    const latestChangelog = getLatestChangelog({ language: user?.language ?? LanguageCode.EN });
    if (!latestChangelog) return;

    try {
      await setLastChangelogSeen();
    } catch (err) {
      console.error('Failed to mark changelogs as read:', err);
    }
  }, [setLastChangelogSeen, user?.language]);

  return {
    lastViewedDate: user?.lastChangelogSeen || null,
    hasUnreadChanges,
    markChangelogsAsRead,
    isUpdating,
    error,
  };
};
